import { createClient } from '../client/browser'
import type { SupabaseClient } from '@supabase/supabase-js'
import { v4 as uuidv4 } from 'uuid'
import { findMatchingPros, insertBesReferralsForAlliMatch, updateMatchingQuizWithPros } from './matching'

export interface QuizData {
  ageVerification: 'yes' | 'no'
  sessionType: 'individual' | 'couples'
  needs: string[]
  insurance: string[]
  therapistType: 'resident' | 'associate'
  rate: number
  timezone: string
  availability: string[]
  province?: string
  promoCode?: string
  school?: string
  partnershipId?: string
  isPaidReferrer?: boolean
  referralSource?: string
}


/**
 * Saves quiz data for an authenticated user
 * This is used when user authentication is handled separately from quiz submission
 */
export async function saveQuizData(
  userId: string,
  data: QuizData,
  supabase?: SupabaseClient
): Promise<{ success: boolean; quizId?: string; error?: string }> {
  const client = supabase || createClient()
  
  console.log('[saveQuizData] Received data:', {
    userId,
    ageVerification: data.ageVerification,
    sessionType: data.sessionType,
    needs: data.needs,
    insurance: data.insurance,
    therapistType: data.therapistType,
    rate: data.rate,
    timezone: data.timezone,
    availability: data.availability
  })
  
  try {
    // Validate rate based on tier
    if (data.therapistType === 'resident' && (data.rate < 45 || data.rate > 105)) {
      return {
        success: false,
        error: 'Rate for resident therapist must be between $45 and $105'
      };
    }
    
    if (data.therapistType === 'associate' && data.rate < 115) {
      return {
        success: false,
        error: 'Rate for associate therapist must be $115 or higher'
      };
    }
    
    // Update user's timezone
    const { error: updateError } = await client
      .from('users')
      .update({
        timezone: data.timezone,
        updatedAt: new Date().toISOString()
      })
      .eq('id', userId)
    
    if (updateError) {
      console.error('Error updating user timezone:', updateError)
      // Don't fail the whole operation if timezone update fails
    }
    
    // Create matching quiz record with mapped fields
    const quizId = uuidv4()
    
    // Validate and fix rate based on tier
    let validatedRate = data.rate
    if (data.therapistType === 'associate' && data.rate < 115) {
      console.log('[saveQuizData] Warning: Associate tier with rate below $115, adjusting to minimum of $115')
      validatedRate = 115
    } else if (data.therapistType === 'resident' && data.rate > 105) {
      console.log('[saveQuizData] Warning: Resident tier with rate above $105, adjusting to maximum of $105')
      validatedRate = 105
    }
    
    console.log('[saveQuizData] Inserting matching quiz with insurance:', data.insurance, 'rate:', validatedRate)
    
    const { error: matchError } = await client
      .from('matching_quizzes')
      .insert({
        id: quizId,
        userId,
        type: data.sessionType,
        tier: data.therapistType,
        rate: validatedRate,
        concerns: data.needs, // Now an array of strings
        times: data.availability,
        timezone: data.timezone,
        province: data.province || null,
        insurance: data.insurance, // Keep all values including 'none'
        promoCode: data.promoCode || null,
        school: data.school || null,
        partnershipId: data.partnershipId || null,
        isPaidReferrer: data.isPaidReferrer || false,
        referralSource: data.referralSource || null,
        status: 'pending',
        matchedProIds: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
    
    if (matchError) {
      console.error('Error creating matching quiz record:', matchError)
      return { 
        success: false, 
        error: 'Failed to save quiz data' 
      }
    }
    
    // Find matching pros — enable BES top-up for client-app quiz submissions.
    const matchingCriteria = {
      sessionType: data.sessionType,
      therapistTier: data.therapistType,
      rate: data.rate,
      timezone: data.timezone,
      availability: data.availability,
      insurance: data.insurance,
      province: data.province,
    }

    // BES top-up is only for associate-tier client-app quizzes.
    // - Resident tier ($45-$105) doesn't fit BES market rates ($130-$175+) —
    //   rate mismatch would surface unaffordable pros.
    // - Partnership quizzes (imwell / mhap / employer) stay on the partner's
    //   covered therapist pool, not the open BES network.
    const includeBesFallback = data.therapistType === 'associate' && !data.partnershipId

    console.log('Finding matches for criteria:', matchingCriteria)
    const { proIds, besProIds, besMatchReasons, error: matchingError } = await findMatchingPros(
      matchingCriteria,
      client,
      { includeBesFallback },
    )

    if (matchingError) {
      console.error('Error finding matches:', matchingError)
      // Don't fail the whole operation, just log the error
    } else {
      console.log('Found pro IDs:', proIds, 'BES:', besProIds)
    }

    // Update the quiz with matched internal pro IDs
    if (proIds && proIds.length > 0) {
      console.log('Updating quiz', quizId, 'with pro IDs:', proIds)
      const { error: updateError } = await updateMatchingQuizWithPros(quizId, proIds, client)
      if (updateError) {
        console.error('Error updating quiz with matches:', updateError)
      } else {
        console.log('Successfully updated quiz with matches')
      }
    } else {
      console.log('No pro IDs found to update')
    }

    // Persist bes_referrals rows for BES pros surfaced by the top-up.
    if (besProIds && besProIds.length > 0) {
      const { data: userRow } = await client
        .from('users')
        .select('firstName, lastName, preferredName, email, phone')
        .eq('id', userId)
        .maybeSingle()

      const u = (userRow ?? {}) as { firstName?: string; lastName?: string; preferredName?: string; email?: string; phone?: string }
      const displayFirst = u.preferredName ?? u.firstName
      if (displayFirst && u.email) {
        await insertBesReferralsForAlliMatch(
          {
            matchingQuizId: quizId,
            clientUserId: userId,
            clientFirstName: displayFirst,
            clientLastName: u.lastName ?? null,
            clientEmail: u.email,
            clientPhone: u.phone ?? null,
            clientProvince: data.province ?? null,
            besProIds,
            besMatchReasons,
            criteria: {
              sessionType: data.sessionType,
              tier: data.therapistType,
              rateCeiling: validatedRate,
              province: data.province ?? null,
              timezone: data.timezone,
              availability: data.availability,
              insurance: data.insurance,
              focusAreas: data.needs,
            },
          },
          client,
        )
      } else {
        console.warn('[saveQuizData] Skipping bes_referrals insert — user missing firstName/email', { userId })
      }
    }

    return { success: true, quizId }
    
  } catch (error) {
    console.error('Unexpected error saving quiz data:', error)
    return { 
      success: false, 
      error: 'An unexpected error occurred' 
    }
  }
}

